import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Pagination, PermissionsProvider} from '@/components';
import type {PaginationProps} from '@/components/common/Pagination';
import {TableSkeleton} from '@/components/Table/TableSkeleton';
import {useAppRates} from '@/context/AppRatesContext';
import PromoTiersTable from '@/screens/Rates/Promo/PromoTiersTable';
import TierUpdateButton from '@/screens/Rates/Promo/TierUpdateButton';
import useTierList from '@/screens/Rates/Promo/useTierList';

import BusinessScopeNotice from '../BusinessScopeNotice';

type BusinessRatesTabProps = {
  businessId: string;
  businessName?: string;
  /** Test hook prefix; each rendered element namespaces under it. */
  dataCy?: string;
};

const BusinessRatesTab = ({
  businessId,
  businessName,
  dataCy,
}: BusinessRatesTabProps) => {
  const {state} = useAppRates();
  const {
    isLoading,
    fetchList,
    refetchCurrentPage,
    displayPage,
    pageInfo,
    list,
    isPageReset,
    isSearch,
  } = useTierList('BUSINESS', businessId, Boolean(businessId));

  const label = businessName || 'this enterprise';

  const renderList = () => {
    if (isLoading) {
      return <TableSkeleton />;
    }

    if (!list || !list.length) {
      return (
        <p
          data-cy={dataCy ? `${dataCy}-empty` : undefined}
          className="py-10 text-center text-gray-500 dark:text-gray-400"
        >
          No rate promos found for this enterprise.
        </p>
      );
    }

    return (
      <PromoTiersTable
        items={list}
        rawAppRates={state.rawAppRates}
        businessId={businessId}
        businessName={businessName}
        onFeeSaved={refetchCurrentPage}
        dataCy={dataCy}
      />
    );
  };

  return (
    <section className="px-3 pt-3">
      <div data-cy={dataCy ? `${dataCy}-scope-notice` : undefined}>
        <BusinessScopeNotice
          lead="Showing rate promos specific to"
          name={label}
          tail=". Organization default rate promos are managed under Merchants > Rates."
        />
      </div>

      <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h3
          data-cy={dataCy ? `${dataCy}-title` : undefined}
          className="font-semibold text-gray-900 dark:text-gray-100"
        >
          Enterprise Rate Promos — {label}
        </h3>
        <div className="flex md:justify-end">
          {/* Hold the create button until businessId resolves: with an empty
              id the modal would skip the BUSINESSID scoping tag and create an
              unscoped BUSINESS-target promo. */}
          {businessId ? (
            <PermissionsProvider
              permission={AfriexAdminPermission.RatePromoManage}
            >
              <TierUpdateButton
                dataCy={dataCy ? `${dataCy}-create` : undefined}
                formDataCy={dataCy ? `${dataCy}-form` : undefined}
                businessId={businessId}
                businessName={businessName}
                target="BUSINESS"
                rawAppRates={state.rawAppRates}
                onSuccess={refetchCurrentPage}
              />
            </PermissionsProvider>
          ) : null}
        </div>
      </div>

      <div className="pt-5 md:pt-5">{renderList()}</div>

      {list && list.length ? (
        <Pagination
          onPageChange={fetchList as PaginationProps['onPageChange']}
          total={pageInfo.total}
          isSearch={isSearch}
          isPageReset={isPageReset}
          currentPageOverride={displayPage}
        />
      ) : null}
    </section>
  );
};

export default BusinessRatesTab;
